import {
  Component,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';

import { IPageInfo } from '@iharbeck/ngx-virtual-scroller';
import { Store } from '@ngrx/store';
import { startWith } from 'rxjs/operators';

import { AttrDataScope, TagsActions, tagsSelectors } from '@core/tags-and-sources-data';
import type { AttributesLoadMore, SelectOption } from '@core/user-interface-legacy';
import { BasicMultiSelectComponent } from '@core/user-interface-legacy';

@Component({
  selector: 'rt-candidate-tags-multi-select-wrapper',
  templateUrl: './candidate-tags-multi-select-wrapper.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BasicMultiSelectComponent],
})
export class CandidateTagsMultiSelectWrapperComponent {
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  public readonly LOAD_MORE_WHEN_LEFT = 5;
  public readonly limit = 100;

  public control = input.required<FormControl<string[] | number[]>>();
  public isInvalid = input<boolean>(false);
  public isReadOnly = input<boolean>(false);
  public preselectedOptions = input<SelectOption[]>([]);

  public selectedOptions = output<SelectOption[]>();

  public options = signal<SelectOption[]>([]);
  public page = signal<number>(1);
  public query = signal<string>('');
  public isLoading = this.store.selectSignal(
    tagsSelectors.isPending({
      scope: AttrDataScope.Typeahead,
    }),
  );

  public ngOnInit(): void {
    this.initialLoad();

    const paginatedTags$ = this.store.select(
      tagsSelectors.paginatedAttributeSelector({
        scope: AttrDataScope.Typeahead,
      }),
    );

    paginatedTags$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(paginatedTags => {
      if (paginatedTags.meta) {
        this.page.set(paginatedTags.meta.page!);
      }

      this.options.set(
        paginatedTags.rows.map(attr => ({
          label: attr.value,
          value: attr.searchableName,
        })),
      );
    });

    this.control()
      .valueChanges.pipe(startWith(this.control().value), takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        const selectedOptions = (value || [])
          .map(tagValue =>
            [...this.preselectedOptions(), ...this.options()].find(
              option => option.value === tagValue,
            ),
          )
          .filter(option => !!option);
        this.selectedOptions.emit(selectedOptions);
      });
  }

  public loadMoreTags(params: AttributesLoadMore): void {
    if (this.isLoading()) {
      return;
    }
    this.store.dispatch(
      TagsActions.fetchPaginatedCollection({
        payload: { params, scope: AttrDataScope.Typeahead, add: true },
      }),
    );
  }

  public onScrollChange(event: IPageInfo) {
    const isCloseToScrollEnd =
      event.endIndexWithBuffer > this.options().length - this.LOAD_MORE_WHEN_LEFT;
    const canLoadMore = this.page() * this.limit === this.options().length;
    if (isCloseToScrollEnd && canLoadMore) {
      this.loadMoreTags({
        page: this.page() + 1,
        limit: this.limit,
        query: this.query(),
      });
    }
  }

  public onSearch(query: string): void {
    this.query.set(query);

    const params = {
      query,
      page: 1,
      limit: 100,
    };

    this.store.dispatch(
      TagsActions.fetchPaginatedCollection({
        payload: {
          params,
          scope: AttrDataScope.Typeahead,
        },
      }),
    );
  }

  private initialLoad(): void {
    this.store.dispatch(
      TagsActions.fetchPaginatedCollection({
        payload: {
          params: { page: this.page(), limit: this.limit },
          scope: AttrDataScope.Typeahead,
          add: true,
        },
      }),
    );
  }
}
